import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { createApi } from 'ReduxHelpers';
import {
    type StreamIndexResponse,
    type StreamIndexParams,
    type LessonIndexResponse,
    type LessonsForEditorListParams,
    type CourseIndexResponse,
} from './Lessons.types';

// This API is intended to be used in the editor. In order to prevent us from importing editor-only code
// into the student bundle, there is a separate LessonsEditorApi
export const lessonsEditorApi = createApi('lessonsEditorApi', {
    ...getBackRoyalApiBaseConfig('lessonsEditorApi'),
    endpoints: builder => ({
        /*
            Flatten out all of the lessons in the stream index call into an object
            that has enough information to build a link to the lesson. This assumes that
            each lesson only appears in a single stream for a given user, since the
            response is keyed off of lessonId
        */
        getStreams: builder.query<StreamIndexResponse, StreamIndexParams>({
            query: params => ({
                url: 'lesson_streams/index.json',
                method: 'POST',
                body: params,
            }),
        }),

        /*
            Every course an editor can point at: the recommended and related pickers, the
            translation links and the equivalent-course list all read this one list.

            getStreams cannot serve it, because its response type is the student-facing Stream, which
            has neither wasPublished nor the locale pack's requirementIdentifier. Both are real
            fields of Lesson::Stream::ToJsonFromApiParams; only the student type is narrower.
        */
        getCoursesForEditorList: builder.query<CourseIndexResponse, void>({
            query: () => ({
                url: 'lesson_streams/index.json',
                method: 'POST',
                body: {
                    // The locale filters are off, so the response covers every locale. The
                    // translation links need that.
                    filters: { published: false, userCanSee: null, inUsersLocaleOrEn: null },
                    fields: ['id', 'title', 'tag', 'locale', 'localePack', 'wasPublished'],
                },
            }),
        }),

        /*
            The lessons an editor can put into a course. Lesson#index is a GET, because only
            Lesson::Stream overrides index to a POST.

            The field list goes in a `fields[]` key, not `fields`. serializeParams hands arrays to
            qs.stringify, which repeats the key once per entry, and Rack keeps only the last value
            of a repeated scalar param. Written as `fields`, the server read one field per request:
            every lesson came back as { test: false }, with no title to show and no assessment flag
            to check. The bracketed name is what Rack parses into an array, and snakeCaseApiParams
            has a branch for that exact key.
        */
        getLessonsForEditorList: builder.query<LessonIndexResponse, LessonsForEditorListParams>({
            query: ({ locale }) => ({
                url: 'lessons.json',
                params: {
                    filters: {
                        published: false,
                        inUsersLocaleOrEn: null,
                        archived: false,
                        isPractice: false,
                        locale,
                    },
                    'fields[]': ['id', 'title', 'tag', 'locale', 'assessment', 'test'],
                },
            }),
        }),
    }),
});
